"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  ProductIssueTypeSchema,
  type CreateShopProductClaimDTO,
} from "@/modules/merchant/domain/contracts";
import {
  canUseMerchantPermission,
  shopPosApi,
  shopSuppliersApi,
  type PosSaleListItem,
  type SupplierListRow,
} from "@/modules/merchant/presentation/api/client";
import {
  useCreateShopProductClaim,
  useShopBranchesListQuery,
} from "@/modules/merchant/presentation/api/queries";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateLocal } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  Input,
  Label,
  Loader,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon, PackageIcon, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Resolver } from "react-hook-form";
import { Controller, useFieldArray } from "react-hook-form";
import { z } from "zod";
import {
  SHOP_PRODUCT_CLAIM_WARRANTY_SELECT_NONE,
  saleLineWarrantyStatusLabel,
} from "./shop-product-claim-helpers";

const LIST_ROUTE = "/app/shops/$shopId/product-claims" as const;

const optionalUuidField = z
  .union([z.literal(""), z.string().uuid()])
  .optional()
  .transform((v) => (v === "" || v === undefined ? undefined : v));

const ClaimCreateFormSchema = z
  .object({
    branchId: z.string().uuid(),
    supplierId: optionalUuidField,
    issueType: ProductIssueTypeSchema,
    title: z.string().max(255).optional(),
    description: z.string().max(8000).optional(),
    posSaleId: z
      .union([z.literal(""), z.string().uuid(), z.null()])
      .optional()
      .transform((v) => (v === "" || v === undefined || v === null ? null : v)),
    saleLineWarrantyId: optionalUuidField,
    /** W3 — ເລກໄມລ໌ຕອນເປີດເຄສ (km) */
    odometerAtClaimKm: z.string().max(32).optional(),
    /** W3 — ລູກຄ້າ/ຍານພາຫະນະ */
    vehicleNote: z.string().max(500).optional(),
    lines: z
      .array(
        z.object({
          shopProductSkuId: z.string().min(1, "ຕ້ອງມີ shop product SKU ID"),
          qty: z.coerce.number().int().positive(),
          posSaleItemId: optionalUuidField,
          inventoryLotId: optionalUuidField,
          inventorySerialId: optionalUuidField,
          /** ສະແດງຢ່າງດຽວ — ບໍ່ສົ່ງ API */
          displayProductName: z.string().max(500).optional(),
          displayShopSku: z.string().max(200).optional(),
          /** ຄີຮູບສິນຄ້າ — ສະແດງຢ່າງດຽວ */
          displayImageUrl: z.string().max(2048).optional(),
          /** true ເມື່ອເຕີມຈາກບິນຂາຍ — ແຍກຈາກການກອກມືດ້ວຍ UUID */
          lineFromPosSale: z.boolean().optional(),
          /** ຈຳນວນຊື້ໃນບິນ — ບໍ່ສົ່ງ API; ຈຳກັດ qty ບໍ່ໃຫ້ເກີນ */
          maxQtyFromSaleLine: z.number().int().positive().optional(),
          /** ລະຫັດ lot / serial ສະແດງ — ບໍ່ສົ່ງ API */
          displayLotNo: z.string().max(200).optional(),
          displaySerialNo: z.string().max(200).optional(),
        }),
      )
      .min(0),
  })
  .superRefine((data, ctx) => {
    if (data.lines.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງເຕີມລາຍການຈາກບິນຂາຍ (ເຕີມຈາກບິນນີ້)",
        path: ["lines"],
      });
      return;
    }
    data.lines.forEach((line, i) => {
      if (line.lineFromPosSale !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ລາຍການຕ້ອງມາຈາກບິນຂາຍເທົ່ານັ້ນ",
          path: ["lines", i],
        });
      }
      const sku = line.shopProductSkuId.trim();
      if (!z.string().uuid().safeParse(sku).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ຕ້ອງເປັນ UUID",
          path: ["lines", i, "shopProductSkuId"],
        });
      }
      if (
        line.maxQtyFromSaleLine != null &&
        line.qty > line.maxQtyFromSaleLine
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `ຈຳນວນຕ້ອງບໍ່ເກີນ ${line.maxQtyFromSaleLine} (ຕາມບິນຂາຍ)`,
          path: ["lines", i, "qty"],
        });
      }
    });
    const rawOdo = data.odometerAtClaimKm?.trim();
    if (rawOdo && rawOdo.length > 0) {
      const n = Number.parseInt(rawOdo, 10);
      if (!Number.isFinite(n) || n < 0 || n > 9_999_999) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ເລກໄມລ໌ບໍ່ຖືກຕ້ອງ (0–9,999,999 km)",
          path: ["odometerAtClaimKm"],
        });
      }
    }
  });

type ClaimCreateFormValues = z.infer<typeof ClaimCreateFormSchema>;

function issueTypeLabel(t: string): string {
  const m: Record<string, string> = {
    defect: "ຊຳຮຸດ",
    wrong_part: "ສິນຄ້າຜິດ",
    short_ship: "ຂາດຈຳນວນ",
    expired: "ໝົດອາຍຸ/Lot",
    other: "ອື່ນໆ",
  };
  return m[t] ?? t;
}

type NewClaimSearch = {
  branchId?: string;
  supplierId?: string;
};

const SALE_LOOKUP_PAGE_SIZE = 20;

function posSaleDetailToListItem(d: {
  id: string;
  saleNumber: string;
  branchId: string;
  status: string;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  taxPercent: number;
  total: number;
  currency: string;
  createdAt: string;
  holdExpiresAt: string | null;
}): PosSaleListItem {
  return {
    id: d.id,
    saleNumber: d.saleNumber,
    branchId: d.branchId,
    status: d.status,
    subtotal: d.subtotal,
    discountTotal: d.discountTotal,
    taxTotal: d.taxTotal,
    taxPercent: d.taxPercent,
    total: d.total,
    currency: d.currency,
    createdAt: d.createdAt,
    holdExpiresAt: d.holdExpiresAt,
  };
}

export function ShopProductClaimNewPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate();
  const search = useSearch({
    from: "/app/shops/$shopId/product-claims/new",
  }) as NewClaimSearch;

  const { currentShop, currentPermissions } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const defaultBranchId = currentShop?.branchId ?? "";

  const branchesQuery = useShopBranchesListQuery(shopId);
  const branches = branchesQuery.data ?? [];

  const branchFromUrl = search.branchId?.trim() ?? "";
  const supplierFromUrl = search.supplierId?.trim() ?? "";
  const effectiveBranchId =
    branchFromUrl ||
    defaultBranchId ||
    (branches.length === 1 ? branches[0]?.id : "") ||
    "";

  const branchOptions = useMemo(
    () =>
      branches.map((b) => ({
        value: b.id,
        label: b.name,
      })),
    [branches],
  );

  const createClaim = useCreateShopProductClaim(shopId);
  const canReadPos = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.pos.read,
    verifiedLevel,
  );

  const [pickedSaleId, setPickedSaleId] = useState("");
  /** YYYY-MM-DD — ກອງບິນຕາມວັນ (ຕ້ອງມີກ່ອນຄົ້ນຫາ) */
  const [saleFilterDate, setSaleFilterDate] = useState("");

  const createForm = RHF.useForm<ClaimCreateFormValues>({
    resolver: zodResolver(
      ClaimCreateFormSchema,
    ) as Resolver<ClaimCreateFormValues>,
    defaultValues: {
      branchId: effectiveBranchId || "",
      supplierId: supplierFromUrl || "",
      issueType: "defect",
      title: "",
      description: "",
      posSaleId: null,
      saleLineWarrantyId: undefined,
      odometerAtClaimKm: "",
      vehicleNote: "",
      lines: [],
    },
  });

  const branchIdForPos =
    createForm.watch("branchId") || effectiveBranchId || "";

  const saleComboboxQueryKey = useMemo(
    () => ["pos-sale-claim", shopId ?? "", branchIdForPos, saleFilterDate],
    [shopId, branchIdForPos, saleFilterDate],
  );

  const saleLookupQueryFn = useCallback(
    async ({ search, pageParam }: { search: string; pageParam: number }) => {
      const id = shopId;
      if (!id || !branchIdForPos || !saleFilterDate) {
        return {
          items: [] as PosSaleListItem[],
          nextPage: null as number | null,
        };
      }
      const limit = SALE_LOOKUP_PAGE_SIZE;
      const offset = (pageParam - 1) * limit;
      const res = await shopPosApi.listSales(id, {
        branchId: branchIdForPos,
        createdFrom: saleFilterDate,
        createdTo: saleFilterDate,
        saleNumber: search.trim() || undefined,
        limit,
        offset,
      });
      const hasMore = offset + res.items.length < res.meta.total;
      return {
        items: res.items,
        nextPage: hasMore ? pageParam + 1 : null,
      };
    },
    [shopId, branchIdForPos, saleFilterDate],
  );

  const salePreloadQueryFn = useCallback(
    async (saleId: string) => {
      if (!shopId || !saleId) return null;
      const d = await shopPosApi.getSale(shopId, saleId);
      return posSaleDetailToListItem(d);
    },
    [shopId],
  );

  const saleDetailForClaimQuery = useQuery({
    queryKey: ["pos-sale-claim-new", shopId, pickedSaleId],
    queryFn: async () => {
      const id = shopId;
      if (!id) throw new Error("shopId required");
      return shopPosApi.getSale(id, pickedSaleId);
    },
    enabled: !!shopId && !!pickedSaleId,
  });

  const lineArray = useFieldArray({
    control: createForm.control,
    name: "lines",
  });

  const saleLineWarrantyIdWatch = createForm.watch("saleLineWarrantyId");
  const selectedWarrantyMeta = useMemo(() => {
    const lines = saleDetailForClaimQuery.data?.lines ?? [];
    for (const l of lines) {
      if (l.warranty?.id === saleLineWarrantyIdWatch) return l.warranty;
    }
    return null;
  }, [saleDetailForClaimQuery.data, saleLineWarrantyIdWatch]);

  const warrantySelectOptions = useMemo(() => {
    const lines = saleDetailForClaimQuery.data?.lines ?? [];
    const opts = lines.flatMap((l) => {
      const w = l.warranty;
      if (!w) return [];
      return [
        {
          value: w.id,
          label: `${l.productName} · ຫມົດ ${formatDateLocal(w.coverageEndAt)}`,
        },
      ];
    });
    return [
      {
        value: SHOP_PRODUCT_CLAIM_WARRANTY_SELECT_NONE,
        label: "ບໍ່ອ້າງສິດປະກັນຕາມບິນ",
      },
      ...opts,
    ];
  }, [saleDetailForClaimQuery.data]);

  useEffect(() => {
    if (lineArray.fields.length === 0) {
      createForm.setValue("saleLineWarrantyId", undefined);
    }
  }, [lineArray.fields.length, createForm]);

  useEffect(() => {
    if (effectiveBranchId) {
      createForm.setValue("branchId", effectiveBranchId);
    }
  }, [effectiveBranchId, createForm]);

  useEffect(() => {
    if (supplierFromUrl) {
      createForm.setValue("supplierId", supplierFromUrl);
    }
  }, [supplierFromUrl, createForm]);

  useEffect(() => {
    setPickedSaleId("");
  }, [branchIdForPos, saleFilterDate]);

  const onCreateSubmit = async (vals: ClaimCreateFormValues) => {
    if (!shopId) return;
    const optUuid = (s: string | undefined) => {
      const t = s?.trim();
      return t && t.length > 0 ? t : null;
    };
    const odoRaw = vals.odometerAtClaimKm?.trim();
    const odometerAtClaimKm =
      odoRaw && odoRaw.length > 0 ? Number.parseInt(odoRaw, 10) : undefined;
    const body: CreateShopProductClaimDTO = {
      branchId: vals.branchId,
      supplierId: optUuid(vals.supplierId ?? undefined),
      issueType: vals.issueType,
      title: vals.title?.trim() || undefined,
      description: vals.description?.trim() || undefined,
      posSaleId: vals.posSaleId ?? null,
      saleLineWarrantyId: optUuid(vals.saleLineWarrantyId ?? undefined),
      odometerAtClaimKm,
      vehicleNote: vals.vehicleNote?.trim() || undefined,
      lines: vals.lines.map((l) => ({
        shopProductSkuId: l.shopProductSkuId.trim(),
        qty: l.qty,
        posSaleItemId: optUuid(l.posSaleItemId ?? undefined),
        inventoryLotId: optUuid(l.inventoryLotId ?? undefined),
        inventorySerialId: optUuid(l.inventorySerialId ?? undefined),
        lineNote: undefined,
      })),
    };
    await createClaim.mutateAsync(body);
    void nav({
      to: LIST_ROUTE,
      params: { shopId },
      search: {
        offset: 0,
        limit: 20,
        branchId: vals.branchId,
        status: "draft",
        supplierId: optUuid(vals.supplierId ?? undefined) ?? undefined,
        warrantyLink: undefined,
      },
    });
  };

  const fillLinesFromPickedSale = () => {
    const d = saleDetailForClaimQuery.data;
    if (!d?.lines.length) return;
    createForm.setValue("posSaleId", d.id);
    createForm.setValue("saleLineWarrantyId", undefined);
    createForm.setValue("odometerAtClaimKm", "");
    createForm.setValue("vehicleNote", "");
    lineArray.replace(
      d.lines.map((l) => ({
        shopProductSkuId: l.skuId,
        qty: l.qty,
        posSaleItemId: l.id,
        inventoryLotId: l.lotId ?? undefined,
        inventorySerialId: l.serialId ?? undefined,
        displayProductName: l.productName,
        displayShopSku: l.shopSku,
        displayImageUrl: l.imageUrl ?? undefined,
        lineFromPosSale: true,
        maxQtyFromSaleLine: l.qty,
        displayLotNo: l.lotNo ?? undefined,
        displaySerialNo: l.serialNo ?? undefined,
      })),
    );
  };

  if (!shopId) return null;

  return (
    <RequireMerchantPermission permission={MerchantPermissions.claims.create}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.productClaimsNew(shopId)} />
      </Header>

      <Main>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-heading font-semibold text-xl tracking-tight">
              ສ້າງເຄສສິນຄ້າມີບັນຫາ
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ບັນທຶກເປັນຮ່າງ ຜູກບິນຂາຍ (ທາງເລືອກ) ແລ້ວເປີດລາຍລະອຽດເຄສ ເພື່ອແນບຫຼັກຖານ ແລະ
              ກົດສົ່ງ
            </p>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mb-2 -ml-2 h-8 gap-1"
            onClick={() =>
              void nav({
                to: LIST_ROUTE,
                params: { shopId },
                search: {
                  offset: 0,
                  limit: 20,
                  branchId: effectiveBranchId || undefined,
                  status: undefined,
                  supplierId: supplierFromUrl || undefined,
                  warrantyLink: undefined,
                },
              })
            }
          >
            <ArrowLeftIcon className="size-4" />
            ກັບລາຍການ
          </Button>
        </div>

        {!effectiveBranchId ? (
          <p className="text-muted-foreground text-sm">
            ເລືອກສາຂາໃນບັນຊີ ຫຼື ເປີດໜ້ານີ້ຈາກລາຍການເຄສ (ມີສາຂາໃນ URL).
          </p>
        ) : (
          <div className="rounded-xl border bg-card p-4 sm:p-6">
            <FormRoot<ClaimCreateFormValues>
              methods={createForm}
              onSubmit={(v) => onCreateSubmit(v)}
              className="space-y-4"
            >
              {/* <div className="space-y-2">
                <Label>ສາຂາ</Label>
                <SimpleSelect
                  value={createForm.watch("branchId")}
                  onValueChange={(v) => createForm.setValue("branchId", v)}
                  className="h-9 min-w-[200px]"
                  placeholder="ສາຂາ"
                  disabled={branchOptions.length === 0}
                  options={branchOptions}
                />
              </div> */}
              <div className="grid gap-2 sm:grid-cols-2">
                <FormSelect
                  name="branchId"
                  label="ສາຂາ"
                  options={branchOptions}
                  placeholder="ສາຂາ"
                  requiredMark
                />

                <FormSelect
                  name="issueType"
                  label="ປະເພດບັນຫາ"
                  options={[
                    { value: "defect", label: issueTypeLabel("defect") },
                    {
                      value: "wrong_part",
                      label: issueTypeLabel("wrong_part"),
                    },
                    {
                      value: "short_ship",
                      label: issueTypeLabel("short_ship"),
                    },
                    { value: "expired", label: issueTypeLabel("expired") },
                    { value: "other", label: issueTypeLabel("other") },
                  ]}
                />
              </div>
              <Controller
                name="supplierId"
                control={createForm.control}
                render={({ field }) => (
                  <div className="space-y-2">
                    <Label>ຜູ້ສະໜອງ (ທາງເລືອກ)</Label>
                    <InfiniteCombobox<SupplierListRow>
                      queryKey={[
                        "shops",
                        shopId ?? "",
                        "product-claims",
                        "new",
                        "supplier",
                      ]}
                      queryFn={async ({ search: q, pageParam }) => {
                        if (!shopId) {
                          return {
                            items: [] as SupplierListRow[],
                            nextPage: null,
                          };
                        }
                        const res = await shopSuppliersApi.list(shopId, {
                          limit: 20,
                          offset: (pageParam - 1) * 20,
                          search: q.trim() || undefined,
                          activeOnly: true,
                        });
                        const hasMore =
                          res.meta.offset + res.data.length < res.meta.total;
                        return {
                          items: res.data,
                          nextPage: hasMore ? pageParam + 1 : null,
                        };
                      }}
                      preloadQueryFn={async (id) => {
                        if (!shopId || !id) return null;
                        const res = await shopSuppliersApi.list(shopId, {
                          limit: 200,
                          offset: 0,
                          activeOnly: false,
                        });
                        return res.data.find((r) => r.id === id) ?? null;
                      }}
                      getLabel={(item) => item.name}
                      getValue={(item) => item.id}
                      value={field.value ?? ""}
                      onValueChange={field.onChange}
                      placeholder="ຜູ້ສະໜອງ"
                      className="w-full max-w-md"
                      clearable
                    />
                  </div>
                )}
              />
              <FormInput name="title" label="ຫົວຂໍ້ (ທາງເລືອກ)" placeholder="ສັ້ນໆ" />

              <FormTextarea
                name="description"
                label="ລາຍລະອຽດ (ທາງເລືອກ)"
                placeholder="ສັ້ນໆ"
                rows={4}
              />
              {canReadPos ? (
                <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
                  <Label>ບິນຂາຍ POS (ທາງເລືອກ)</Label>
                  <p className="text-muted-foreground text-xs">
                    ເລືອກວັນທີກ່ອນ — ຄົ້ນຫາບິນຕາມວັນ ແລະເລກບິນໃນຊ່ອງຄົ້ນຫາ — ຈາກນັ້ນກົດ
                    ເຕີມເພື່ອຜູກລາຍການ, SKU, ຈຳນວນ, lot/serial
                  </p>

                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input
                      id="sale-filter-date"
                      type="date"
                      value={saleFilterDate}
                      onChange={(e) => setSaleFilterDate(e.target.value)}
                    />
                    {!saleFilterDate ? (
                      <Button
                        type="button"
                        variant="outline"
                        disabled
                        className="w-full justify-between opacity-60"
                      >
                        ເລືອກວັນທີກ່ອນຄົ້ນຫາບິນ...
                      </Button>
                    ) : (
                      <InfiniteCombobox<PosSaleListItem>
                        queryKey={saleComboboxQueryKey}
                        queryFn={saleLookupQueryFn}
                        preloadQueryFn={salePreloadQueryFn}
                        getLabel={(s) =>
                          `${s.saleNumber} · ${formatDateLocal(s.createdAt)}`
                        }
                        getValue={(s) => s.id}
                        value={pickedSaleId}
                        onValueChange={(v) => setPickedSaleId(v)}
                        placeholder="ຄົ້ນຫາເລກບິນ ແລ້ວເລືອກ..."
                        className="w-full min-w-0"
                        clearable
                      />
                    )}
                  </div>
                  {saleDetailForClaimQuery.isLoading && pickedSaleId ? (
                    <p className="text-muted-foreground text-sm">
                      ກໍາລັງໂຫຼດລາຍລະອຽດບິນ...
                    </p>
                  ) : null}
                  {saleDetailForClaimQuery.data ? (
                    <p className="text-muted-foreground text-xs">
                      {saleDetailForClaimQuery.data.lines.length} ລາຍການໃນບິນ ·{" "}
                      {saleDetailForClaimQuery.data.saleNumber}
                    </p>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    disabled={
                      !pickedSaleId ||
                      !saleDetailForClaimQuery.data?.lines.length
                    }
                    onClick={() => fillLinesFromPickedSale()}
                  >
                    ເຕີມຈາກບິນນີ້
                  </Button>
                </div>
              ) : null}
              {canReadPos &&
              lineArray.fields.length > 0 &&
              saleDetailForClaimQuery.data ? (
                <div className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-3">
                  <Label>ສິດປະກັນຕາມບິນ (ທາງເລືອກ)</Label>
                  <p className="text-muted-foreground text-xs">
                    ເລືອກວ່າເຄສນີ້ອ້າງສິດປະກັນຕາມແຖວໃດ — ບັນທຶກເປັນຮ່າງກ່ອນ,
                    ສະຖານະຄຸ້ມຄອງຈະຖືກຄຳນວນເມື່ອສົ່ງ
                  </p>
                  <SimpleSelect
                    value={
                      createForm.watch("saleLineWarrantyId") ??
                      SHOP_PRODUCT_CLAIM_WARRANTY_SELECT_NONE
                    }
                    onValueChange={(v) =>
                      createForm.setValue(
                        "saleLineWarrantyId",
                        v === SHOP_PRODUCT_CLAIM_WARRANTY_SELECT_NONE
                          ? undefined
                          : v,
                        { shouldValidate: true },
                      )
                    }
                    options={warrantySelectOptions}
                    placeholder="ເລືອກສິດປະກັນ"
                    className="w-full max-w-md"
                    disabled={warrantySelectOptions.length <= 1}
                  />
                </div>
              ) : null}
              {canReadPos && lineArray.fields.length > 0 ? (
                <div className="space-y-3 rounded-lg border border-border/60 bg-muted/15 p-3">
                  <Label className="text-foreground">ລູກຄ້າ ແລະ ເລກໄມລ໌ (W3)</Label>
                  <p className="text-muted-foreground text-xs">
                    ແນະນຳໃສ່ເລກໄມລ໌ເມື່ອປະກັນມີເກນກິໂລແມັດ — ຂໍ້ຄວາມລຸ້ນ/ທະບຽນເປັນທາງເລືອກ
                  </p>
                  {selectedWarrantyMeta?.coverageEndOdometerKm != null ? (
                    <p className="text-sky-700 text-xs dark:text-sky-300">
                      ເກນໄມລ໌ປະກັນ: ບໍ່ເກີນ{" "}
                      {selectedWarrantyMeta.coverageEndOdometerKm.toLocaleString()}{" "}
                      km
                    </p>
                  ) : null}
                  <FormInput
                    name="odometerAtClaimKm"
                    label="ເລກໄມລ໌ຕອນເປີດເຄສ (km)"
                    placeholder="ຕົວເລກເຕັມ"
                    type="text"
                    inputMode="numeric"
                  />
                  <FormTextarea
                    name="vehicleNote"
                    label="ລູກຄ້າ / ຍານພາຫະນະ (ທາງເລືອກ)"
                    placeholder="ຊື່, ເບີລົດ, ລຸ້ນລົດ..."
                    rows={2}
                  />
                </div>
              ) : null}
              <div className="space-y-3">
                <Label>ລາຍການສິນຄ້າໃນເຄສ</Label>
                {lineArray.fields.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    ເລືອກບິນຂາຍ ແລະ ກົດ &quot;ເຕີມຈາກບິນນີ້&quot; ເພື່ອເພີ່ມລາຍການ
                  </p>
                ) : null}
                {createForm.formState.errors.lines &&
                typeof createForm.formState.errors.lines === "object" &&
                "message" in createForm.formState.errors.lines &&
                typeof createForm.formState.errors.lines.message ===
                  "string" ? (
                  <p className="text-destructive text-sm" role="alert">
                    {createForm.formState.errors.lines.message}
                  </p>
                ) : null}
                {lineArray.fields.map((field, index) => {
                  const lineRow = createForm.watch(`lines.${index}`);
                  return (
                    <div
                      key={field.id}
                      className="relative overflow-hidden rounded-xl border border-border/70 bg-gradient-to-br from-muted/30 via-background to-background shadow-sm ring-1 ring-border/40"
                    >
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-3 right-3"
                        onClick={() => lineArray.remove(index)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                      <div className="flex gap-3 p-3 pr-12 sm:gap-4 sm:p-4 sm:pr-14">
                        <div className="relative size-24 shrink-0 overflow-hidden rounded-lg border border-border/60 bg-muted/80 shadow-inner sm:size-28">
                          <AppImage
                            src={lineRow?.displayImageUrl}
                            alt={lineRow?.displayProductName?.trim() || "ສິນຄ້າ"}
                            className="size-full"
                            fit="cover"
                            fallback={
                              <div
                                className="flex size-full items-center justify-center bg-muted"
                                aria-hidden
                              >
                                <PackageIcon className="size-10 text-muted-foreground/70" />
                              </div>
                            }
                          />
                        </div>
                        <div className="min-w-0 flex-1 space-y-3">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                            <div className="min-w-0 flex-1 space-y-2">
                              <h3 className="font-heading font-semibold text-foreground text-sm leading-snug tracking-tight sm:text-base">
                                {lineRow?.displayProductName?.trim() || "—"}
                              </h3>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-muted-foreground text-xs">
                                  ລະຫັດຮ້ານ
                                </span>
                                <code className="rounded-md border border-border/80 bg-muted/50 px-2 py-0.5 font-mono text-foreground text-xs">
                                  {lineRow?.displayShopSku?.trim() || "—"}
                                </code>
                              </div>
                              {canReadPos && saleDetailForClaimQuery.data ? (
                                <p className="text-muted-foreground text-xs">
                                  {(() => {
                                    const sl =
                                      saleDetailForClaimQuery.data?.lines.find(
                                        (x) => x.id === lineRow?.posSaleItemId,
                                      );
                                    const w = sl?.warranty;
                                    if (!w) return "ບັນທັດນີ້ບໍ່ມີສິດປະກັນຕາມບິນ";
                                    return `ປະກັນຫມົດ ${formatDateLocal(w.coverageEndAt)} · ${saleLineWarrantyStatusLabel(w.status)}`;
                                  })()}
                                </p>
                              ) : null}
                            </div>
                            <div className="w-full shrink-0 sm:w-33 sm:p-2.5">
                              <FormInput
                                name={`lines.${index}.qty`}
                                label="ຈຳນວນ"
                                requiredMark
                                type="number"
                                min={1}
                                max={lineRow?.maxQtyFromSaleLine}
                                step={1}
                              />
                              {lineRow?.maxQtyFromSaleLine != null ? (
                                <p className="mt-1.5 text-[11px] text-muted-foreground leading-tight">
                                  ສູງສຸດ {lineRow.maxQtyFromSaleLine} ຊິ້ນ (ຕາມບິນ)
                                </p>
                              ) : null}
                            </div>
                          </div>
                          {lineRow?.inventoryLotId ||
                          lineRow?.inventorySerialId ? (
                            <div className="flex flex-wrap gap-2 border-border/50 border-t pt-2">
                              {lineRow.inventoryLotId ? (
                                <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-primary/25 bg-primary/5 px-2 py-1 text-xs">
                                  <span className="shrink-0 font-medium text-muted-foreground">
                                    Lot no.
                                  </span>
                                  <span className="min-w-0 truncate font-mono text-foreground">
                                    {lineRow.displayLotNo?.trim() || "—"}
                                  </span>
                                </span>
                              ) : null}
                              {lineRow.inventorySerialId ? (
                                <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-primary/25 bg-primary/5 px-2 py-1 text-xs">
                                  <span className="shrink-0 font-medium text-muted-foreground">
                                    Serial no.
                                  </span>
                                  <span className="min-w-0 truncate font-mono text-foreground">
                                    {lineRow.displaySerialNo?.trim() || "—"}
                                  </span>
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <p className="border-border/50 border-t pt-2 text-muted-foreground text-xs">
                              ບໍ່ມີ lot / serial ກັບບັນທັດຂາຍນີ້
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    void nav({
                      to: LIST_ROUTE,
                      params: { shopId },
                      search: {
                        offset: 0,
                        limit: 20,
                        branchId: effectiveBranchId || undefined,
                        status: undefined,
                        supplierId: supplierFromUrl || undefined,
                        warrantyLink: undefined,
                      },
                    })
                  }
                >
                  ຍົກເລີກ
                </Button>
                <Button type="submit" disabled={createClaim.isPending}>
                  {createClaim.isPending ? <Loader /> : "ບັນທຶກຮ່າງ"}
                </Button>
              </div>
            </FormRoot>
          </div>
        )}
      </Main>
    </RequireMerchantPermission>
  );
}
